import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

import { hubLaborExpectedPageFromPathname } from "@/lib/labor/laborPageKey";
import {
  clearLaborReadOnlyStayIfPageDiffers,
  getLaborReadOnlyStaySnapshot,
  subscribeLaborReadOnlyStay,
} from "@/lib/labor/laborReadOnlyStay";
import { laborPageKeysEquivalent } from "@/pages/clampqc/clampQcLaborClockState";

const READ_ONLY_STYLE_ID = "labor-readonly-stay-style";

function laborReadOnlyStayShouldBlockTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest("main") == null) return false;
  const control = target.closest("button, [role='button']");
  if (control == null) return false;
  if (control.closest("[data-labor-readonly-allow], th, [role='tab']")) return false;
  return true;
}

export function useLaborReadOnlyStayActive(): boolean {
  const { pathname, search } = useLocation();
  const activePage = useSyncExternalStore(subscribeLaborReadOnlyStay, getLaborReadOnlyStaySnapshot, () => null);
  const pageKey = hubLaborExpectedPageFromPathname(pathname, search);
  return activePage != null && laborPageKeysEquivalent(activePage, pageKey);
}

/**
 * Banner plus a block on work buttons while this tab is in labor read-only stay.
 * Filters, table-header sort, and tabs marked data-labor-readonly-allow still work.
 */
export function LaborReadOnlyStayNotice(): JSX.Element | null {
  const { pathname, search } = useLocation();
  const pageKey = hubLaborExpectedPageFromPathname(pathname, search);
  const active = useLaborReadOnlyStayActive();

  useEffect(() => {
    clearLaborReadOnlyStayIfPageDiffers(pageKey);
  }, [pageKey]);

  useEffect(() => {
    const main = document.querySelector("main.hub-main");
    if (!(main instanceof HTMLElement)) return;
    if (active) main.setAttribute("data-labor-readonly", "true");
    else main.removeAttribute("data-labor-readonly");
    return () => {
      main.removeAttribute("data-labor-readonly");
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const blockClick = (event: Event): void => {
      if (!laborReadOnlyStayShouldBlockTarget(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const blockKey = (event: KeyboardEvent): void => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!laborReadOnlyStayShouldBlockTarget(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const blockSubmit = (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("main") == null) return;
      if (target.closest("[data-labor-readonly-allow]")) return;
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener("click", blockClick, true);
    document.addEventListener("keydown", blockKey, true);
    document.addEventListener("submit", blockSubmit, true);
    return () => {
      document.removeEventListener("click", blockClick, true);
      document.removeEventListener("keydown", blockKey, true);
      document.removeEventListener("submit", blockSubmit, true);
    };
  }, [active]);

  if (!active) return null;

  return (
    <>
      <style id={READ_ONLY_STYLE_ID}>{`
        main[data-labor-readonly="true"] button,
        main[data-labor-readonly="true"] [role="button"] {
          pointer-events: none;
          opacity: 0.55;
        }
        main[data-labor-readonly="true"] [data-labor-readonly-allow] button,
        main[data-labor-readonly="true"] [data-labor-readonly-allow] [role="button"],
        main[data-labor-readonly="true"] button[data-labor-readonly-allow],
        main[data-labor-readonly="true"] th button,
        main[data-labor-readonly="true"] [role="tab"] {
          pointer-events: auto;
          opacity: 1;
        }
      `}</style>
      <p
        role="status"
        className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
      >
        Read-only. You clocked out from another page, so this station stays open and work actions are off.
        Leave this page and open it again when you want to clock back in.
      </p>
    </>
  );
}
